import { useState, useEffect } from "react";
import { Users, Plus, Pencil, Trash2, UserPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  getTeams,
  getPlayers,
  createPlayer,
  createPlayersBulk,
  updatePlayer,
  deletePlayer,
} from "../lib/api";
import { toast } from "sonner";

const POSITIONS = [
  "Goalkeeper",
  "Defender",
  "Midfielder",
  "Forward",
];

export default function Players() {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [formData, setFormData] = useState({
    team_id: "",
    name: "",
    number: "",
    position: "",
  });
  const [bulkData, setBulkData] = useState({
    team_id: "",
    playerNames: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, playersRes] = await Promise.all([
        getTeams(),
        getPlayers(),
      ]);
      setTeams(teamsRes.data);
      setPlayers(playersRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const filteredPlayers =
    selectedTeamId === "all"
      ? players
      : players.filter((p) => p.team_id === selectedTeamId);

  const getTeamName = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.name || "Unknown";
  };

  const handleOpenDialog = (player = null) => {
    if (player) {
      setSelectedPlayer(player);
      setFormData({
        team_id: player.team_id,
        name: player.name,
        number: player.number?.toString() || "",
        position: player.position || "",
      });
    } else {
      setSelectedPlayer(null);
      setFormData({
        team_id: selectedTeamId !== "all" ? selectedTeamId : "",
        name: "",
        number: "",
        position: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPlayer(null);
    setFormData({ team_id: "", name: "", number: "", position: "" });
  };

  const handleOpenBulkDialog = () => {
    setBulkData({
      team_id: selectedTeamId !== "all" ? selectedTeamId : "",
      playerNames: "",
    });
    setBulkDialogOpen(true);
  };

  const handleCloseBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkData({ team_id: "", playerNames: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.team_id || !formData.name) {
      toast.error("Team and player name are required");
      return;
    }

    setSaving(true);
    try {
      const data = {
        team_id: formData.team_id,
        name: formData.name,
        number: formData.number ? parseInt(formData.number, 10) : null,
        position: formData.position || null,
      };

      if (selectedPlayer) {
        await updatePlayer(selectedPlayer.id, data);
        toast.success("Player updated successfully");
      } else {
        await createPlayer(data);
        toast.success("Player added successfully");
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save player");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!bulkData.team_id || !bulkData.playerNames.trim()) {
      toast.error("Team and player names are required");
      return;
    }

    const playerNames = bulkData.playerNames
      .split("\n")
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    if (playerNames.length === 0) {
      toast.error("Please enter at least one player name");
      return;
    }

    setSaving(true);
    try {
      await createPlayersBulk({
        team_id: bulkData.team_id,
        players: playerNames,
      });
      toast.success(`${playerNames.length} players added successfully`);
      handleCloseBulkDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add players");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlayer) return;

    try {
      await deletePlayer(selectedPlayer.id);
      toast.success("Player deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedPlayer(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete player");
    }
  };

  const openDeleteDialog = (player) => {
    setSelectedPlayer(player);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="players-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
            Players
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage players across all teams ({filteredPlayers.length} players)
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleOpenBulkDialog}
            variant="secondary"
            data-testid="bulk-add-players-btn"
            disabled={teams.length === 0}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Bulk Add
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
            data-testid="add-player-btn"
            disabled={teams.length === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Player
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Filter by Team
        </Label>
        <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
          <SelectTrigger className="w-48 bg-card border-border" data-testid="team-filter">
            <SelectValue placeholder="All Teams" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">All Teams</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Players List */}
      {filteredPlayers.length > 0 ? (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-4 text-left">Name</th>
                <th className="p-4 text-left">Team</th>
                <th className="p-4 text-center w-20">#</th>
                <th className="p-4 text-left w-32">Position</th>
                <th className="p-4 text-right w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlayers.map((player, index) => (
                <tr
                  key={player.id}
                  className="border-t border-border hover:bg-secondary/30 transition-colors animate-fade-in opacity-0"
                  style={{ animationDelay: `${index * 20}ms` }}
                  data-testid={`player-row-${player.id}`}
                >
                  <td className="p-4">
                    <span className="font-medium">{player.name}</span>
                  </td>
                  <td className="p-4 text-muted-foreground">
                    {getTeamName(player.team_id)}
                  </td>
                  <td className="p-4 text-center">
                    {player.number ? (
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-primary/10 text-primary font-bold rounded-lg text-sm">
                        {player.number}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    {player.position ? (
                      <span className="text-sm text-muted-foreground">
                        {player.position}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(player)}
                        data-testid={`edit-player-${player.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(player)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-player-${player.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Users className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold uppercase mb-2">
            No Players Yet
          </h3>
          <p className="text-muted-foreground mb-6">
            {teams.length === 0
              ? "Create a team first, then add players"
              : "Add players to your teams"}
          </p>
          {teams.length > 0 && (
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleOpenBulkDialog}
                variant="secondary"
                data-testid="empty-bulk-add-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Bulk Add Players
              </Button>
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
                data-testid="empty-add-player-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Player
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Single Player Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="bg-card border-border sm:max-w-md"
          data-testid="player-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-wider">
              {selectedPlayer ? "Edit Player" : "Add Player"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Team *
              </Label>
              <Select
                value={formData.team_id}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, team_id: value }))
                }
              >
                <SelectTrigger
                  className="bg-background border-border"
                  data-testid="player-team-select"
                >
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Player Name *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Enter player name"
                className="bg-background border-border"
                data-testid="player-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Number <span className="text-muted-foreground/50">(optional)</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="99"
                  value={formData.number}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, number: e.target.value }))
                  }
                  placeholder="1-99"
                  className="bg-background border-border"
                  data-testid="player-number-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Position <span className="text-muted-foreground/50">(optional)</span>
                </Label>
                <Select
                  value={formData.position || "none"}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, position: value === "none" ? "" : value }))
                  }
                >
                  <SelectTrigger
                    className="bg-background border-border"
                    data-testid="player-position-select"
                  >
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="none">None</SelectItem>
                    {POSITIONS.map((pos) => (
                      <SelectItem key={pos} value={pos}>
                        {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCloseDialog}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                data-testid="save-player-btn"
              >
                {saving ? "Saving..." : selectedPlayer ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Players Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent
          className="bg-card border-border sm:max-w-md"
          data-testid="bulk-player-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-wider">
              Bulk Add Players
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Select Team *
              </Label>
              <Select
                value={bulkData.team_id}
                onValueChange={(value) =>
                  setBulkData((prev) => ({ ...prev, team_id: value }))
                }
              >
                <SelectTrigger
                  className="bg-background border-border"
                  data-testid="bulk-team-select"
                >
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Player Names *
              </Label>
              <Textarea
                value={bulkData.playerNames}
                onChange={(e) =>
                  setBulkData((prev) => ({ ...prev, playerNames: e.target.value }))
                }
                placeholder="Enter one player name per line:&#10;John Smith&#10;Jane Doe&#10;Mike Johnson"
                className="bg-background border-border min-h-[200px] font-mono text-sm"
                data-testid="bulk-player-names-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter one player name per line. Number and position can be added later.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCloseBulkDialog}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                data-testid="save-bulk-players-btn"
              >
                {saving ? "Adding..." : "Add Players"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-bold uppercase">
              Delete Player
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedPlayer?.name}? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-player-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
